import React, { useState, useEffect } from 'react';
import { Star, X, Loader2 } from 'lucide-react';
import { Button } from './Button';
import { api } from '../lib/axios';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';

interface BreederReviewModalProps {
    isOpen: boolean;
    onClose: () => void;
    breederId: string;
    breedingEventId?: string | null;
    breederName?: string;
    onSuccess?: () => void;
}

export const BreederReviewModal: React.FC<BreederReviewModalProps> = ({
    isOpen,
    onClose,
    breederId,
    breedingEventId,
    breederName,
    onSuccess
}) => {
    const [rating, setRating] = useState(0);
    const [hover, setHover] = useState(0);
    const [comment, setComment] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isLoadingReview, setIsLoadingReview] = useState(false);
    const [hasExistingReview, setHasExistingReview] = useState(false);
    const { t } = useTranslation();

    useEffect(() => {
        if (!isOpen || !breederId) {
            setRating(0);
            setHover(0);
            setComment('');
            setHasExistingReview(false);
            return;
        }

        let isMounted = true;
        const fetchExisting = async () => {
            try {
                setIsLoadingReview(true);
                const res = await api.get(`/breeder/${breederId}/my-review`, {
                    params: breedingEventId ? { breedingEventId } : undefined
                });
                if (!isMounted) return;
                if (res.data?.review) {
                    setRating(Number(res.data.review.rating) || 0);
                    setComment(res.data.review.comment || '');
                    setHasExistingReview(true);
                } else {
                    setRating(0);
                    setComment('');
                    setHasExistingReview(false);
                }
            } catch (err) {
                console.error('Failed to load existing review:', err);
            } finally {
                if (isMounted) setIsLoadingReview(false);
            }
        };

        fetchExisting();
        return () => {
            isMounted = false;
        };
    }, [isOpen, breederId, breedingEventId]);

    if (!isOpen) return null;

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (rating <= 0) {
            toast.error(t('reviews.selectRating', 'Please select a rating'));
            return;
        }

        setIsSubmitting(true);
        try {
            await api.post('/breeder/reviews', {
                breederId,
                breedingEventId: breedingEventId || undefined,
                rating,
                comment: comment.trim() || undefined
            });

            toast.success(
                hasExistingReview
                    ? t('reviews.reviewUpdated', 'Review updated successfully!')
                    : t('reviews.feedbackThanks', 'Thank you for your feedback!')
            );
            if (onSuccess) onSuccess();
            onClose();
        } catch (error: any) {
            toast.error(error.response?.data?.error || t('reviews.submitFailed', 'Failed to submit review'));
        } finally {
            setIsSubmitting(false);
        }
    };

    const activeRating = hover || rating;

    const getRatingLabel = (r: number) => {
        if (r >= 5) return t('reviews.excellent', 'Excellent!');
        if (r >= 4) return t('reviews.veryGood', 'Very Good');
        if (r >= 3) return t('reviews.good', 'Good');
        if (r >= 2) return t('reviews.fair', 'Fair');
        if (r >= 1) return t('reviews.poor', 'Poor');
        return '';
    };

    const handleStarMouseMove = (e: React.MouseEvent<HTMLDivElement>, star: number) => {
        const rect = e.currentTarget.getBoundingClientRect();
        const x = e.clientX - rect.left;
        const val = x < rect.width / 2 ? star - 0.5 : star;
        setHover(val);
    };

    const handleStarClick = (e: React.MouseEvent<HTMLDivElement>, star: number) => {
        const rect = e.currentTarget.getBoundingClientRect();
        const x = e.clientX - rect.left;
        const val = x < rect.width / 2 ? star - 0.5 : star;
        setRating(val);
        setHover(0);
    };

    const getStarFill = (star: number, active: number): 'full' | 'half' | 'empty' => {
        if (active >= star) return 'full';
        if (active >= star - 0.5) return 'half';
        return 'empty';
    };

    return (
        <div className="fixed inset-0 bg-slate-950/40 backdrop-blur-xl z-[100] flex items-center justify-center p-4 animate-in fade-in duration-300">
            <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden animate-in zoom-in-95 duration-200">
                <div className="p-4 border-b border-slate-100 flex justify-between items-center bg-slate-50">
                    <div className="flex items-center gap-2">
                        <h2 className="font-bold text-slate-800">
                            {hasExistingReview 
                                ? t('reviews.updateBreederReview', 'Update Breeder Review')
                                : t('reviews.rateBreederService', 'Rate Breeder Service')}
                        </h2>
                        {isLoadingReview && <Loader2 className="w-3.5 h-3.5 animate-spin text-brand-600" />}
                    </div>
                    <button onClick={onClose} className="p-1 hover:bg-slate-200 rounded-full transition-colors">
                        <X size={18} className="text-slate-500" />
                    </button>
                </div>

                <form onSubmit={handleSubmit} className="p-6 space-y-6">
                    <div className="text-center">
                        <p className="text-sm text-slate-500 mb-4 uppercase tracking-widest font-black text-[10px]">
                            {t('reviews.breederExperiencePrompt', 'How was your experience with {{name}}?', {
                                name: (breederName || t('reviews.theBreeder', 'the Breeder')).toUpperCase()
                            })}
                        </p>
                        <div className="flex justify-center gap-2">
                            {[1, 2, 3, 4, 5].map((star) => {
                                const fill = getStarFill(star, activeRating);
                                return (
                                    <div
                                        key={star}
                                        className="relative cursor-pointer transition-transform hover:scale-110 active:scale-95 w-10 h-10"
                                        onMouseMove={(e) => handleStarMouseMove(e, star)}
                                        onMouseLeave={() => setHover(0)}
                                        onClick={(e) => handleStarClick(e, star)}
                                    >
                                        {/* Background (empty) star */}
                                        <Star className="w-10 h-10 text-gray-200 absolute inset-0" />

                                        {/* Half-filled star overlay */}
                                        {fill === 'half' && (
                                            <div className="absolute inset-0 overflow-hidden" style={{ width: '50%' }}>
                                                <Star className="w-10 h-10 text-yellow-400 fill-yellow-400" />
                                            </div>
                                        )}

                                        {/* Full star overlay */}
                                        {fill === 'full' && (
                                            <Star className="w-10 h-10 text-yellow-400 fill-yellow-400 absolute inset-0" />
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                        <div className="mt-3 h-5">
                            {activeRating > 0 && (
                                <span className="text-xs font-bold text-yellow-600 uppercase tracking-widest">
                                    {activeRating} ★ — {getRatingLabel(activeRating)}
                                </span>
                            )}
                        </div>
                    </div>

                    <div>
                        <label className="block text-[10px] font-black text-slate-400 mb-2 uppercase tracking-widest">
                            {t('reviews.feedbackOptional', 'Feedback Comment (Optional)')}
                        </label>
                        <textarea
                            className="w-full rounded-xl border border-slate-200 bg-slate-50 focus:border-brand-500 focus:ring-brand-500 text-sm p-3 min-h-[100px] outline-none transition-all"
                            placeholder={t('reviews.shareExperience', 'Share your experience...')}
                            value={comment}
                            onChange={(e) => setComment(e.target.value)}
                        />
                    </div>

                    <Button
                        fullWidth
                        type="submit"
                        disabled={rating === 0 || isSubmitting}
                        className="bg-brand-600 text-white font-bold rounded-2xl shadow-[0_8px_30px_rgb(0,0,0,0.12)] hover:bg-brand-700 hover:shadow-[0_8px_30px_rgb(0,0,0,0.16)] transition-all hover:-translate-y-0.5 border-transparent h-12"
                    >
                        {isSubmitting ? (
                            <span className="flex items-center justify-center gap-2">
                                <Loader2 className="w-4 h-4 animate-spin" />
                                {hasExistingReview ? t('reviews.updating', 'Updating...') : t('reviews.submitting', 'Submitting...')}
                            </span>
                        ) : rating === 0 ? (
                            t('reviews.selectRating', 'Select a Rating')
                        ) : hasExistingReview ? (
                            t('reviews.updateReview', 'Update Review')
                        ) : (
                            t('reviews.submitReview', 'Submit Review')
                        )}
                    </Button>
                </form>
            </div>
        </div>
    );
};
